// This is a part of the Cadcorp SIS source code. 
// Copyright (c) Cadcorp Limited 1991-2025 
import { Overlay, LayerStatus } from '../GeognoSIS/Overlay';
declare const ol: any;
import { IAjax } from '../../../../CommonScripts/Abstractions';
import GeognosisTileImage from './source/GeognoSISTileImage';
import GeognosisSingleImage from './source/GeognoSISImage';
import GeognosisVectorTile from './source/GeognoSISVector';

/**
 * @class
 * @classdesc A factory to create OpenLayers layers from GeognoSIS overlays.
 */
export class LayerFactory {    
    constructor() { }
    /**
     * Creates an open layers layer of the correct type from a GeognoSIS layer
     * @public
     * @param {Object} overlay The GeognoSIS overlay to create the open layers layer from
     * @param {string} epsgCode The EPSG code for the new layer
     * 
     */
    public createLayer(overlay: Overlay, epsgCode: string, sessionId: string, ajax: IAjax,  tileSize: number[], useSingleTile: boolean, extent: number[] | undefined): any {          
        const layer = this.getNewLayer(overlay, epsgCode, sessionId, ajax, tileSize, useSingleTile, extent);
        this.setRefreshInterval(layer);
        return layer;
    }

    private getNewLayer(overlay: Overlay, epsgCode: string, sessionId: string, ajax: IAjax, tileSize: number[], useSingleTile: boolean, extent: number[] | undefined): any {
        if (overlay.useVector) return this.createVectorLayer(overlay, epsgCode, sessionId, ajax, extent);
        return useSingleTile ? this.createImageLayer(overlay, sessionId, extent) : this.createTileLayer(overlay, epsgCode, sessionId, ajax, tileSize, extent);
    } 

    private setRefreshInterval(layer: any): void {
        const refreshInterval = layer.getSource().overlay.refreshInterval;
        if (refreshInterval == undefined || refreshInterval === 0) return;
        window.setTimeout(() => this.refreshLayer(layer), refreshInterval * 1000);
    }

    private refreshLayer(layer: any): void {        
        const overlay = layer.getSource().overlay;        
        const promise = overlay.refreshOverlay();  // newly added method to Overlay to use instead of using an action in the map store because embedded map can't see the stores.
        promise.then(() => {
            layer.getSource().clear();  // GeognoSISImage (single tile mode) needs this clear() method in addition to refresh()
            layer.getSource().refresh();
            window.setTimeout(() => this.refreshLayer(layer), overlay.refreshInterval * 1000); // perpetuate the timeout rather than set interval
        });        
    }

    /** tile layer */
    private createTileLayer(overlay: Overlay, epsgCode: string, sessionId: string, ajax: IAjax, tileSize: number[], extent: number[] | undefined): any {
        const proj = ol.proj.get(epsgCode);
        const source = new GeognosisTileImage(
        {
            projection: proj,
            crossOrigin: 'anonymous',
            sessionId: sessionId,
            overlay: overlay,
            ajaxWrapper: ajax,
            photometricInterpretation: 'RGBA',
            attributions: overlay.attribution,
            tileGrid: this.createTileGridFromProjection(proj, tileSize)
        });

        const layer = new ol.layer.Tile({
            opacity: 1,
            name: overlay.name,
            visible: overlay.status > LayerStatus.Invisible,
            isBaseLayer: false,
            allowVectorExport: this.getAllowVectorExport(overlay),
            source: source,
            extent: extent
        });
        return layer;
    }

    /** single image layer */
    private createImageLayer(overlay: Overlay, sessionId: string, extent: number[] | undefined): any {
        const url = overlay.getImageUrl();       

        const sourceOptions: any = {
            url: url,
            sessionId: sessionId,
            overlay: overlay,            
            photometricInterpretation: 'RGBA',
            attributions: overlay.attribution,
        };

        const source = new GeognosisSingleImage(sourceOptions);

        const layerOptions: any = {
            opacity: 1,
            name: overlay.name,
            visible: overlay.status > LayerStatus.Invisible,
            isBaseLayer: false,
            allowVectorExport: this.getAllowVectorExport(overlay),
            source: source,
            extent: extent
        };
        return new ol.layer.Image(layerOptions);       
    }    

    private createVectorLayer(overlay: Overlay, epsgCode: string, sessionId: string, ajax: IAjax, extent: number[] | undefined): any {
        let overlayStyle = null;
        if (overlay.vectorStyle) {
            try {
                overlayStyle = JSON.parse(overlay.vectorStyle);
            } catch (ex) {
                console.log(overlay.vectorStyle);
                console.log(ex.toString());
            }
        }
        else {
            overlayStyle = {
                fill: {
                    colour: {r:255,g:0,b:0,a:0.6}
                },
                stroke: {
                    colour: { r: 255, g: 0, b: 0, a: 1 },
                    width:2
                },
                image: {
                    fill: {
                        colour: { r: 255, g: 0, b: 0, a: 0.6 }
                    },
                    stroke: {
                        colour: { r: 255, g: 0, b: 0, a: 1 },
                        width: 2
                    },
                    radius: 10
                }
            };
        }
        
        const layer = new ol.layer.Vector({
            style: this.createOLStyle(overlayStyle),
            opacity: 1,
            name: overlay.name,
            visible: overlay.status > LayerStatus.Invisible,
            isBaseLayer: false,
            allowVectorExport: this.getAllowVectorExport(overlay),
            extent: extent,
            source: new GeognosisVectorTile(
                {
                    cacheSize: 500,
                    projection: ol.proj.get(epsgCode),
                    crossOrigin: 'anonymous',
                    sessionId: sessionId,
                    overlay: overlay,
                    ajaxWrapper: ajax,
                    format: new ol.format.GeoJSON(),
                    strategy: ol.loadingstrategy.tile(ol.tilegrid.createXYZ({ tileSize: 256 })),
                    attributions: overlay.attribution
                })
        });
        return layer;
    }

    private createOLStyle(styleDefinition: any): any {
        const fill = new ol.style.Fill({ color: this.getOlColour(styleDefinition.fill.colour) });
        const stroke = this.getOlStroke(styleDefinition.stroke);
        const style = new ol.style.Style({ fill: fill, stroke: stroke });
        if (styleDefinition?.image) {
            const img = new ol.style.Circle({
                fill: new ol.style.Fill({ color: this.getOlColour(styleDefinition.image.fill.colour) }),
                stroke: this.getOlStroke(styleDefinition.image.stroke),
                radius: styleDefinition.image.radius
            });
            style.setImage(img);
        }
        return style;
    }
    private getOlColour(colourDefinition): number[]{
        return [colourDefinition.r, colourDefinition.g,colourDefinition.b, colourDefinition.a];
    }

    private getOlStroke(strokeDefinition): any {
        const colour = strokeDefinition.colour;
        let dashStyle = null;
        if (strokeDefinition.dash)
            dashStyle = strokeDefinition.dash;
        let lineCap = null;
        if (strokeDefinition.lineCap)
            lineCap = strokeDefinition.lineCap;
        let lineJoin = null;
        if (strokeDefinition.lineCap)
            lineJoin = strokeDefinition.lineCap;
        return new ol.style.Stroke({
            color: this.getOlColour(colour),
            width: strokeDefinition.width,
            lineCap: lineCap,
            lineJoin: lineJoin,
            lineDash: dashStyle
        });
         
    }

    private getAllowVectorExport(overlay: Overlay): boolean {
        return overlay.properties['WML::AllowVectorExport@']
            ? overlay.properties['WML::AllowVectorExport@']
            : false;
    }
    public createTileGridFromProjection(projection: any, size: number[]) {
        const prjExtent = projection.getExtent();
        const resolutions = this.resolutionsFromExtent(prjExtent, size)
        return new ol.tilegrid.TileGrid({ extent: prjExtent, origin: ol.extent.getCorner(prjExtent, this.getCornerString(projection.getAxisOrientation())), tileSize: size, resolutions: resolutions })
    }
    private resolutionsFromExtent(extent,size) {
        var maxZoom = ol.tilegrid.common.DEFAULT_MAX_ZOOM;
        var height = ol.extent.getHeight(extent);
        var width = ol.extent.getWidth(extent);
        var maxResolution = Math.max(width / size[0], height /size[1]);
        var length = maxZoom + 1;
        var resolutions = new Array(length);
        for (var z = 0; z < length; ++z) {
            resolutions[z] = maxResolution / Math.pow(2, z);
        }
        return resolutions;
    }
    private getCornerString(axisOrientation: string): string {
        let retVal: string = null;
        switch (axisOrientation) {
            default:
                retVal = 'top-left';
                break
        }

        return retVal;
    }
}

